import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { getDashboardPath } from "../../config/dashboardRoutes";
import { login, clearError } from "./authSlice";
import { resolveAuthPermissionId, resolveAuthRoleId } from "./authUtils";
import { returnPath } from "./returnPath";
import { schema, type LoginFormData } from "./schema";
import { AnimatedLogo } from "../../components/ui/AnimatedLogo";
import { AnimatedBackgroundPanel } from "../../components/ui/AnimatedBackgroundPanel";
import { AnimatedButton } from "../../components/ui/AnimatedButton";
import { AnimatedInput, AnimatedPasswordInput } from "../../components/ui/AnimatedInputs";
import { tokenHandler } from "../../utils/tokenHandler";
import Logo from "../../assets/login/roundlogo.png";
import TruckMaleva from "../../assets/login/truckmaleva.jpg";

export default function Login() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { user, loading, error } = useAppSelector((state) => state.auth);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      username: "",
      password: "",
    },
  });

  useEffect(() => {
    if (user && tokenHandler.isValid(0)) {
      // Logged out by the system (timeout, expired session) while on a screen:
      // go back to it. Moved with the History API, the same way the forced
      // logout left it, because navigate() re-encodes a query string
      // (?Id=9 arrives as ?Id=%229%22).
      const resumePath = returnPath.consume(user);
      if (resumePath) {
        window.history.replaceState(null, "", resumePath);
        window.dispatchEvent(new PopStateEvent("popstate"));
        return;
      }

      navigate(getDashboardPath(resolveAuthRoleId(user), resolveAuthPermissionId(user)), {
        replace: true,
      });
    }
  }, [navigate, user]);

  useEffect(() => {
    return () => {
      dispatch(clearError());
    };
  }, [dispatch]);

  const onSubmit = (data: LoginFormData) => {
    void dispatch(
      login({
        username: data.username,
        password: data.password,
      })
    );
  };

  return (
    <div className="min-h-screen flex">
      <div className="w-full md:w-1/2 flex flex-col justify-center px-12 md:px-24 bg-linear-to-br from-white to-gray-50 animate-fade-in-left">
        <AnimatedLogo
          onClick={() => navigate("/")}
          logoSrc={Logo}
          title="Maleva"
          subtitle="Ship Spare in Transit"
        />

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 animate-slide-up-form">
          {error && (
            <div
              className="px-5 py-4 rounded-xl text-sm flex items-start gap-3 shadow-sm border"
              style={{
                backgroundColor: "#fee2e2",
                borderColor: "#fecaca",
                borderLeft: "4px solid #dc2626",
                color: "#b91c1c",
              }}
              role="alert"
              aria-live="polite"
            >
              <svg className="w-5 h-5 shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="font-semibold">{error}</span>
            </div>
          )}

          <AnimatedInput
            label="Username"
            placeholder="Enter your username"
            animationDelay="0.3s"
            autoComplete="username"
            {...register("username")}
            error={errors.username?.message}
          />

          <AnimatedPasswordInput
            label="Password"
            placeholder="••••••••"
            animationDelay="0.5s"
            autoComplete="current-password"
            {...register("password")}
            error={errors.password?.message}
          />

          <AnimatedButton type="submit" disabled={loading} loading={loading} animationDelay="0.7s">
            Sign In
          </AnimatedButton>
        </form>
      </div>

      <AnimatedBackgroundPanel
        backgroundImage={TruckMaleva}
        title="SHIP SPARE IN TRANSIT"
        subtitle="Powered by Maleva"
        loadingText="Loading your logistics experience..."
      />
    </div>
  );
}
